import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  RootDiv,
  InsuranceItem,
  TitleTypography,
  InformationTypography,
  TextContainer,
  SwitchButton,
  AddInsuranceContainer,
  IconContainer,
  LoaderContainer,
  InsuranceSkeletonContainer,
} from '../styles/InsuranceOptions.styles';
import AddIcon from '@mui/icons-material/Add';
import { useModalRouter } from '@hooks/useModalRouter';
import { Console, getRoute } from '@utils';
import { CircularProgress, Divider } from '@mui/material';
import { useAuth } from '@hooks/useAuth';
import { usePaymentMethodActions } from '../zustand/paymentMethodStore';
import { AddInsuranceTypography } from '../styles/InsuranceOptions.styles';
import { INSURANCE_EMAILS_ALLOWED } from '@config/environment';
import { SkeletonContainer } from '@screens/insurance/my-insurances/styles';
import { StyledSkeleton } from '@components/Reminders/reminderCardSkeleton.styles';
import { usePermissions } from '@hooks/usePermissions';
import { useMekiState } from '@hooks/useMekiState';
import { UserInsurance } from '@interfaces/userInsurance.interface';
import { ApolloError } from '@apollo/client';
import { ErrorTypography } from './PaymentsOptions.styles';
interface InsuranceOptionsProps {
  loadInsuranceQuote: (rut: string) => Promise<void>;
  loadingInsurances: boolean;
  mekiCreditsChecked?: boolean;
  userInsurances: UserInsurance[];
  loadingCircle?: boolean;
  quoteError?: ApolloError;
}

export function InsuranceOptions(props: InsuranceOptionsProps) {
  const {
    loadingInsurances,
    loadInsuranceQuote,
    loadingCircle,
    userInsurances,
    quoteError,
  } = props;
  const { setMekiCreditsChecked } = usePaymentMethodActions();

  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [insuranceClicked, setInsuranceClicked] = useState<number | null>(null);
  const { insuranceId, setInsuranceId, setInsuranceRut } = useMekiState();
  const { appUser: user } = useAuth();
  const { userCan } = usePermissions();

  const modalRouter = useModalRouter();
  const isLocalVendor = userCan('LOCAL_VENDOR');

  const isPermittedUser = !INSURANCE_EMAILS_ALLOWED
    ? true
    : INSURANCE_EMAILS_ALLOWED.includes(user?.email ?? '');

  const selectedInsurance = useMemo(() => {
    return userInsurances.find((insurance) => insurance.id === insuranceId);
  }, [userInsurances, insuranceId]);

  const handleInsuranceChange = async (id: number) => {
    if (selectedInsurance?.id === id) {
      setInsuranceId(null);
      setInsuranceClicked(null);
      setMekiCreditsChecked(false);
      return;
    }
    setInsuranceClicked(id);
    setShowLoader(true);
    const selected = userInsurances.find((insurance) => insurance.id === id);
    if (selected) {
      try {
        setInsuranceId(id);
        setMekiCreditsChecked(false);
        setInsuranceRut(selected.rut);
        await loadInsuranceQuote(selected.rut);
      } catch (err) {
        Console.error('Error loading insurance quote:', err);
        setInsuranceId(null);
        setInsuranceClicked(null);
        setMekiCreditsChecked(false);
      } finally {
        setShowLoader(false);
      }
    }
  };

  const insuranceAmount = useMemo(
    () => userInsurances?.length || 0,
    [userInsurances],
  );

  const insuranceRedirect = () => {
    if (isLocalVendor) {
      return void modalRouter.push(
        getRoute('insuranceIntroduction', {
          from: 'checkout',
          redirect: 'checkoutPaymentMethodLocal',
        }),
      );
    }
    return void modalRouter.push(
      getRoute('insuranceIntroduction', {
        from: 'checkout',
        redirect: 'checkoutPaymentMethod',
      }),
    );
  };

  const sortedUserInsurancesByDate = useMemo(() => {
    return userInsurances.sort(
      (a, b) => new Date(b.usedAt).getTime() - new Date(a.usedAt).getTime(),
    );
  }, [userInsurances]);

  useEffect(() => {
    if (quoteError) {
      setInsuranceId(null);
      setInsuranceClicked(null);
    }
  }, [quoteError]);

  if (!isPermittedUser) {
    return null;
  }

  return (
    <>
      {!loadingInsurances ? (
        <RootDiv hidden={false} childAmount={insuranceAmount}>
          <TitleTypography>Seguro complementario</TitleTypography>
          {sortedUserInsurancesByDate.map((insurance) => (
            <Fragment key={insurance.id}>
              <InsuranceItem key={insurance.id}>
                <TextContainer>
                  <InformationTypography>
                    {insurance.name}
                  </InformationTypography>
                  <InformationTypography>{insurance.rut}</InformationTypography>
                </TextContainer>
                {(showLoader || loadingCircle) &&
                insuranceClicked === insurance.id ? (
                  <LoaderContainer>
                    <CircularProgress size={25} thickness={4} color="primary" />
                  </LoaderContainer>
                ) : (
                  <SwitchButton
                    onChange={() => {
                      void handleInsuranceChange(insurance.id);
                    }}
                    checked={insuranceId && insuranceId === insurance.id}
                  />
                )}
              </InsuranceItem>
              {insuranceAmount != 1 && <Divider />}
            </Fragment>
          ))}
          {insuranceAmount >= 1 && (
            <>
              {insuranceAmount == 1 && <Divider />}
              <AddInsuranceContainer>
                <IconContainer>
                  <AddIcon color="primary" fontSize="small" />
                </IconContainer>
                <AddInsuranceTypography onClick={insuranceRedirect}>
                  Agregar otro seguro/beneficiario
                </AddInsuranceTypography>
              </AddInsuranceContainer>
            </>
          )}
          {insuranceAmount == 0 && (
            <AddInsuranceContainer onClick={insuranceRedirect}>
              <IconContainer>
                <AddIcon color="primary" fontSize="small" />
              </IconContainer>
              <AddInsuranceTypography>
                Agregar seguro/beneficiario
              </AddInsuranceTypography>
            </AddInsuranceContainer>
          )}
        </RootDiv>
      ) : (
        <InsuranceSkeletonContainer>
          <SkeletonContainer>
            <StyledSkeleton
              variant="rectangular"
              width={'10%'}
              height={10}
              style={{ margin: 0 }}
            />
            <StyledSkeleton
              variant="rectangular"
              width={'30%'}
              height={10}
              style={{ margin: 0 }}
            />
            <StyledSkeleton
              variant="rectangular"
              width={'15%'}
              height={10}
              style={{ margin: 0 }}
            />
          </SkeletonContainer>
        </InsuranceSkeletonContainer>
      )}
      {quoteError && (
        <ErrorTypography>
          No se pudo obtener la cotización del seguro. Intente más tarde.
        </ErrorTypography>
      )}
    </>
  );
}
